import React from 'react';
import { Calendar, Clock, AlertCircle, CheckSquare, Info } from 'lucide-react';
import { combineDateAndTime } from '../utils/dateUtils';
import { useTimezone } from '../../../../../hooks/useTimezone';

/**
 * Text field component with consistent styling
 */
export const TextField = ({
  label,
  id,
  name,
  value,
  onChange,
  placeholder,
  error,
  required = false,
  disabled = false,
  maxLength,
  helperText
}) => {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-slate-300 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type="text"
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className={`w-full bg-slate-700 border ${error ? 'border-red-500' : 'border-slate-600'} text-slate-200 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
      />
      {error && (
        <div className="mt-1 text-xs text-red-400 flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" />
          {error}
        </div>
      )}
      {!error && helperText && (
        <div className="mt-1 text-xs text-slate-400">{helperText}</div>
      )}
    </div>
  );
};

/**
 * Select field component with consistent styling
 */
export const SelectField = ({ 
  label, 
  id, 
  name, 
  value, 
  onChange, 
  options, 
  error, 
  required = false,
  disabled = false,
  emptyOption = true,
  valueField = 'id', // Default to using 'id' as the value field
  labelField = 'name' // Default to using 'name' as the label field
}) => {
  // Helper function to determine the option value
  const getOptionValue = (option) => {
    // If option is an object with the specified valueField, use that
    if (typeof option === 'object' && option !== null) {
      if (option.value !== undefined) return option.value; // For backward compatibility
      if (option[valueField] !== undefined) return option[valueField];
    }
    // Otherwise, use the option itself (string or primitive)
    return option;
  };
  
  // Helper function to determine the option label
  const getOptionLabel = (option) => {
    // If option is an object with the specified labelField, use that
    if (typeof option === 'object' && option !== null) {
      if (option.label !== undefined) return option.label; // For backward compatibility
      if (option[labelField] !== undefined) return option[labelField];
    }
    // Otherwise, use the option itself (string or primitive)
    return option;
  };
  
  // Create a modified onChange handler that ensures we pass the raw value,
  // not the complex object, to the parent component
  const handleChange = (e) => {
    // Create a modified event to return just the value, not the complex option object
    const modifiedEvent = {
      ...e,
      target: {
        ...e.target,
        name: e.target.name,
        value: e.target.value
      }
    };
    
    // Call the parent's onChange with our modified event
    onChange(modifiedEvent);
  };
  
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-slate-300 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <select
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        className={`w-full bg-slate-700 border ${error ? 'border-red-500' : 'border-slate-600'} text-slate-200 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
      >
        {emptyOption && <option value="">Select {label}</option>}
        {options.map((option, index) => {
          const optionValue = getOptionValue(option);
          const optionLabel = getOptionLabel(option);
          return (
            <option key={`${optionValue}-${index}`} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
      {error && (
        <div className="mt-1 text-xs text-red-400 flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" />
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * Number field component with consistent styling
 */
export const NumberField = ({ 
  label, 
  id, 
  name, 
  value, 
  onChange, 
  min, 
  max, 
  step = 1, 
  error, 
  required = false,
  disabled = false
}) => {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-slate-300 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type="number"
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className={`w-full bg-slate-700 border ${error ? 'border-red-500' : 'border-slate-600'} text-slate-200 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
      />
      {error && (
        <div className="mt-1 text-xs text-red-400 flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" />
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * Checkbox field component with consistent styling
 */
export const CheckboxField = ({ 
  label, 
  id, 
  name, 
  checked, 
  onChange, 
  error,
  disabled = false
}) => {
  return (
    <div className="mb-4">
      <div className="flex items-center">
        <input
          type="checkbox"
          id={id}
          name={name}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
        />
        <label htmlFor={id} className="ml-2 text-sm font-medium text-slate-300">
          {label}
        </label>
      </div>
      {error && (
        <div className="mt-1 text-xs text-red-400 flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" />
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * Date field component with consistent styling
 */
export const DateField = ({ 
  label, 
  id, 
  name, 
  value, 
  onChange, 
  error, 
  required = false,
  disabled = false,
  minDate
}) => {
  const todayFormatted = new Date().toISOString().split('T')[0];
  
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-slate-300 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <input
          type="date"
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          min={minDate || todayFormatted}
          disabled={disabled}
          className={`w-full bg-slate-700 border ${error ? 'border-red-500' : 'border-slate-600'} text-slate-200 p-2 pl-9 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
        <Calendar className="w-4 h-4 text-slate-400 absolute top-3 left-3" />
      </div>
      {error && (
        <div className="mt-1 text-xs text-red-400 flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" />
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * Time field component with consistent styling
 */
export const TimeField = ({ 
  label, 
  id, 
  name, 
  value, 
  onChange, 
  error, 
  required = false,
  disabled = false
}) => {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-slate-300 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <input
          type="time"
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={`w-full bg-slate-700 border ${error ? 'border-red-500' : 'border-slate-600'} text-slate-200 p-2 pl-9 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
        <Clock className="w-4 h-4 text-slate-400 absolute top-3 left-3" />
      </div>
      {error && (
        <div className="mt-1 text-xs text-red-400 flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" />
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * Date and time fields combined in one row
 */
export const DateTimeField = ({ 
  dateLabel, 
  dateId, 
  dateName, 
  dateValue, 
  timeLabel, 
  timeId, 
  timeName, 
  timeValue, 
  onChange, 
  dateError, 
  timeError, 
  required = false,
  disabled = false,
  minDate
}) => {
  // Handle changes to either date or time field
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({
      target: {
        name,
        value
      }
    });
  };

  return (
    <div className="mb-4 flex flex-col md:flex-row gap-4">
      <div className="md:w-2/3">
        <DateField
          label={dateLabel}
          id={dateId}
          name={dateName}
          value={dateValue}
          onChange={handleChange}
          error={dateError}
          required={required}
          disabled={disabled}
          minDate={minDate}
        />
      </div>
      <div className="md:w-1/3">
        <TimeField
          label={timeLabel}
          id={timeId}
          name={timeName}
          value={timeValue}
          onChange={handleChange}
          error={timeError}
          required={required}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

/**
 * Textarea field component with consistent styling
 */
export const TextareaField = ({ 
  label, 
  id, 
  name, 
  value, 
  onChange, 
  rows = 3, 
  error, 
  required = false,
  disabled = false,
  placeholder
}) => {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-slate-300 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <textarea
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        rows={rows}
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full bg-slate-700 border ${error ? 'border-red-500' : 'border-slate-600'} text-slate-200 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
      />
      {error && (
        <div className="mt-1 text-xs text-red-400 flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" />
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * InfoBox component for displaying form messages
 */
export const InfoBox = ({ type = 'info', children }) => {
  let bgColor = 'bg-blue-900/30';
  let borderColor = 'border-blue-800';
  let textColor = 'text-blue-200';
  let Icon = Info;
  
  if (type === 'warning') {
    bgColor = 'bg-yellow-900/30';
    borderColor = 'border-yellow-800';
    textColor = 'text-yellow-200';
    Icon = AlertCircle;
  } else if (type === 'error') {
    bgColor = 'bg-red-900/30';
    borderColor = 'border-red-800';
    textColor = 'text-red-200';
    Icon = AlertCircle;
  } else if (type === 'success') {
    bgColor = 'bg-green-900/30';
    borderColor = 'border-green-800';
    textColor = 'text-green-200';
    Icon = CheckSquare;
  }
  
  return (
    <div className={`p-3 ${bgColor} border ${borderColor} rounded-lg ${textColor} mb-4 flex items-start`}>
      <Icon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
      <div>{children}</div>
    </div>
  );
};

/**
 * Form button component with consistent styling
 */
export const FormButton = ({ 
  type = 'button', 
  onClick, 
  className = '', 
  disabled = false, 
  isLoading = false,
  children,
  mode = 'inbound'
}) => {
  const baseClass = 'px-4 py-2 rounded-md font-medium flex items-center justify-center';
  const colorClass = mode === 'inbound' 
    ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500' 
    : 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500';
  const disabledClass = 'opacity-50 cursor-not-allowed';
  
  // Handle button click event
  const handleClick = (e) => {
    if (type !== 'submit' && onClick) {
      onClick(e);
    }
  };
  
  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={`${baseClass} ${colorClass} ${disabled || isLoading ? disabledClass : ''} ${className}`}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Processing...
        </>
      ) : children}
    </button>
  );
};

/**
 * Helper for date & time field combination with timezone support
 * @param {string} dateField - Date in YYYY-MM-DD format
 * @param {string} timeField - Time in HH:MM format
 * @param {string} timezone - IANA timezone (optional)
 * @returns {Date} - Combined datetime in UTC
 */
export const combineDateTimeFromFields = (dateField, timeField, timezone) => {
  if (!dateField) return null;
  
  // Log the input values for debugging
  console.log('Combining date and time fields with timezone:', {
    dateField,
    timeField,
    timezone,
    currentTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  
  // Use the utility function to combine date and time with timezone support
  const result = combineDateAndTime(dateField, timeField || '00:00', timezone);
  
  // Log the result for debugging
  if (result) {
    console.log('Combined datetime result:', {
      iso: result.toISOString(),
      localString: result.toString()
    });
  }
  
  return result;
};